import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Timesheet Calendar Component
 *
 * Displays a calendar view of hours worked per day within a pay period.
 * Supports navigation between pay periods with arrow buttons.
 */
import { useMemo } from 'react';
import { format, parseISO, eachDayOfInterval, startOfWeek, endOfWeek, isWithinInterval, differenceInMinutes, isToday, isFuture, } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Clock, } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/**
 * Calculate hours worked for each day based on punches
 */
function calculateDailyHours(punches, periodStart, periodEnd) {
    const dailyHours = new Map();
    // Initialize all days in the period
    const allDays = eachDayOfInterval({ start: periodStart, end: periodEnd });
    for (const day of allDays) {
        const dateKey = format(day, 'yyyy-MM-dd');
        dailyHours.set(dateKey, {
            date: day,
            totalHours: 0,
            regularHours: 0,
            overtimeHours: 0,
            isInPeriod: true,
            punchCount: 0,
            hasIncomplete: false,
        });
    }
    // Sort punches by timestamp
    const sortedPunches = [...punches]
        .filter(p => p.status === 'ACTIVE')
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    // Track open sessions per day
    let openClockIn = null;
    let openBreakStart = null;
    let dailyMinutes = new Map();
    for (const punch of sortedPunches) {
        const punchDate = parseISO(punch.timestamp);
        const dateKey = format(punchDate, 'yyyy-MM-dd');
        // Update punch count
        const dayData = dailyHours.get(dateKey);
        if (dayData) {
            dayData.punchCount++;
        }
        switch (punch.action) {
            case 'CLOCK_IN':
                openClockIn = punch;
                openBreakStart = null;
                break;
            case 'BREAK_START':
                if (openClockIn) {
                    // Add time from clock in or end of last break to now
                    const breakStartTime = parseISO(punch.timestamp);
                    const workStartTime = openBreakStart
                        ? parseISO(openBreakStart.timestamp)
                        : parseISO(openClockIn.timestamp);
                    const minutes = differenceInMinutes(breakStartTime, workStartTime);
                    // Add to the appropriate day(s)
                    addMinutesToDay(dailyMinutes, dateKey, minutes);
                }
                openBreakStart = punch;
                break;
            case 'BREAK_END':
                openBreakStart = null;
                // Track that we're back from break - work time will be calculated at clock out
                break;
            case 'CLOCK_OUT':
                if (openClockIn) {
                    const clockOutTime = parseISO(punch.timestamp);
                    const clockInTime = parseISO(openClockIn.timestamp);
                    // Calculate total session time
                    let totalSessionMinutes = differenceInMinutes(clockOutTime, clockInTime);
                    // Subtract break time (simplified - doesn't handle multi-day sessions perfectly)
                    // For now, just calculate the gross time
                    const sessionMinutes = totalSessionMinutes;
                    // Add to the clock-out day (simplified - works for most cases)
                    addMinutesToDay(dailyMinutes, dateKey, 0); // Ensure day exists
                    // Actually recalculate: get all punches for this session and compute properly
                    const clockInDateKey = format(clockInTime, 'yyyy-MM-dd');
                    // For same-day sessions (most common)
                    if (clockInDateKey === dateKey) {
                        // Get break time for this session
                        const breakMinutes = getBreakMinutesForSession(sortedPunches, openClockIn, punch);
                        const workMinutes = sessionMinutes - breakMinutes;
                        addMinutesToDay(dailyMinutes, dateKey, workMinutes);
                    }
                    else {
                        // Multi-day session (rare) - split at midnight
                        // Simplified: attribute all to clock-out day
                        const breakMinutes = getBreakMinutesForSession(sortedPunches, openClockIn, punch);
                        const workMinutes = sessionMinutes - breakMinutes;
                        addMinutesToDay(dailyMinutes, dateKey, workMinutes);
                    }
                }
                openClockIn = null;
                openBreakStart = null;
                break;
        }
    }
    // Mark days with incomplete sessions
    if (openClockIn) {
        const dateKey = format(parseISO(openClockIn.timestamp), 'yyyy-MM-dd');
        const dayData = dailyHours.get(dateKey);
        if (dayData) {
            dayData.hasIncomplete = true;
        }
        // Also mark today as incomplete if that's where the open session is
        const todayKey = format(new Date(), 'yyyy-MM-dd');
        const todayData = dailyHours.get(todayKey);
        if (todayData) {
            todayData.hasIncomplete = true;
        }
    }
    // Convert minutes to hours and apply to dailyHours
    for (const [dateKey, minutes] of dailyMinutes) {
        const dayData = dailyHours.get(dateKey);
        if (dayData) {
            const hours = minutes / 60;
            dayData.totalHours = hours;
            // Simple OT calculation: >8 hours in a day is overtime
            dayData.regularHours = Math.min(hours, 8);
            dayData.overtimeHours = Math.max(0, hours - 8);
        }
    }
    return dailyHours;
}
function addMinutesToDay(dailyMinutes, dateKey, minutes) {
    const current = dailyMinutes.get(dateKey) || 0;
    dailyMinutes.set(dateKey, current + minutes);
}
function getBreakMinutesForSession(punches, clockIn, clockOut) {
    const clockInTime = new Date(clockIn.timestamp).getTime();
    const clockOutTime = new Date(clockOut.timestamp).getTime();
    let breakMinutes = 0;
    let breakStart = null;
    for (const punch of punches) {
        const punchTime = new Date(punch.timestamp).getTime();
        if (punchTime < clockInTime || punchTime > clockOutTime)
            continue;
        if (punch.action === 'BREAK_START') {
            breakStart = new Date(punch.timestamp);
        }
        else if (punch.action === 'BREAK_END' && breakStart) {
            breakMinutes += differenceInMinutes(new Date(punch.timestamp), breakStart);
            breakStart = null;
        }
    }
    return breakMinutes;
}
/**
 * Format hours for display
 */
function formatHours(hours) {
    if (hours === 0)
        return '-';
    return hours.toFixed(2);
}
export function TimesheetCalendar({ payPeriod, punches, onPreviousPeriod, onNextPeriod, hasPreviousPeriod, hasNextPeriod, isLoading = false, }) {
    const periodStart = parseISO(payPeriod.startDate);
    const periodEnd = parseISO(payPeriod.endDate);
    // Calculate daily hours
    const dailyHours = useMemo(() => {
        return calculateDailyHours(punches, periodStart, periodEnd);
    }, [punches, payPeriod.startDate, payPeriod.endDate]);
    // Get calendar grid (may include days outside the period for alignment)
    const calendarDays = useMemo(() => {
        const calendarStart = startOfWeek(periodStart, { weekStartsOn: 0 });
        const calendarEnd = endOfWeek(periodEnd, { weekStartsOn: 0 });
        return eachDayOfInterval({ start: calendarStart, end: calendarEnd }).map(date => {
            const dateKey = format(date, 'yyyy-MM-dd');
            const periodData = dailyHours.get(dateKey);
            const isInPeriod = isWithinInterval(date, { start: periodStart, end: periodEnd });
            return {
                date,
                dateKey,
                isInPeriod,
                totalHours: periodData?.totalHours || 0,
                regularHours: periodData?.regularHours || 0,
                overtimeHours: periodData?.overtimeHours || 0,
                punchCount: periodData?.punchCount || 0,
                hasIncomplete: periodData?.hasIncomplete || false,
            };
        });
    }, [dailyHours, periodStart, periodEnd]);
    // Group days into weeks
    const weeks = useMemo(() => {
        const result = [];
        for (let i = 0; i < calendarDays.length; i += 7) {
            result.push(calendarDays.slice(i, i + 7));
        }
        return result;
    }, [calendarDays]);
    // Format period display
    const periodDisplay = useMemo(() => {
        return `${format(periodStart, 'MMM d')} - ${format(periodEnd, 'MMM d, yyyy')}`;
    }, [periodStart, periodEnd]);
    const periodTypeLabel = useMemo(() => {
        const typeMap = {
            weekly: 'Weekly',
            biweekly: 'Biweekly',
            semimonthly: 'Semi-monthly',
            monthly: 'Monthly',
        };
        return typeMap[payPeriod.payPeriodType] || payPeriod.payPeriodType;
    }, [payPeriod.payPeriodType]);
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs(Button, { variant: "outline", size: "sm", onClick: onPreviousPeriod, disabled: !hasPreviousPeriod || isLoading, className: "gap-1", children: [_jsx(ChevronLeft, { className: "h-4 w-4" }), "Previous"] }), _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: "flex items-center justify-center gap-2", children: [_jsx(Calendar, { className: "h-5 w-5 text-blue-600" }), _jsx("h2", { className: "text-lg font-semibold", children: periodDisplay })] }), _jsxs("div", { className: "flex items-center justify-center gap-2 mt-1", children: [_jsx("span", { className: "text-sm text-gray-500", children: periodTypeLabel }), _jsx("span", { className: cn("text-xs px-2 py-0.5 rounded-full font-medium", payPeriod.status === 'current'
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-gray-100 text-gray-600"), children: payPeriod.status === 'current' ? 'Current' : 'Completed' })] })] }), _jsxs(Button, { variant: "outline", size: "sm", onClick: onNextPeriod, disabled: !hasNextPeriod || isLoading, className: "gap-1", children: ["Next", _jsx(ChevronRight, { className: "h-4 w-4" })] })] }), _jsxs("div", { className: "grid grid-cols-3 gap-4", children: [_jsxs("div", { className: "bg-blue-50 border border-blue-200 rounded-lg p-4 text-center", children: [_jsx("p", { className: "text-xs text-blue-600 font-medium uppercase tracking-wide", children: "Total Hours" }), _jsx("p", { className: "text-2xl font-bold text-blue-700 mt-1", children: payPeriod.totalHours.toFixed(2) })] }), _jsxs("div", { className: "bg-green-50 border border-green-200 rounded-lg p-4 text-center", children: [_jsx("p", { className: "text-xs text-green-600 font-medium uppercase tracking-wide", children: "Regular" }), _jsx("p", { className: "text-2xl font-bold text-green-700 mt-1", children: payPeriod.regularHours.toFixed(2) })] }), _jsxs("div", { className: "bg-orange-50 border border-orange-200 rounded-lg p-4 text-center", children: [_jsx("p", { className: "text-xs text-orange-600 font-medium uppercase tracking-wide", children: "Overtime" }), _jsx("p", { className: "text-2xl font-bold text-orange-700 mt-1", children: payPeriod.overtimeHours.toFixed(2) })] })] }), _jsxs("div", { className: "border border-gray-200 rounded-lg overflow-hidden", children: [_jsx("div", { className: "grid grid-cols-7 bg-gray-50 border-b border-gray-200", children: WEEKDAYS.map(day => (_jsx("div", { className: "py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider", children: day }, day))) }), _jsx("div", { className: "divide-y divide-gray-200", children: weeks.map((week, weekIndex) => (_jsx("div", { className: "grid grid-cols-7 divide-x divide-gray-200", children: week.map((day) => {
                                const isDayToday = isToday(day.date);
                                const isDayFuture = isFuture(day.date);
                                const hasHours = day.totalHours > 0;
                                const hasOvertime = day.overtimeHours > 0;
                                return (_jsxs("div", { className: cn("min-h-[80px] p-2 transition-colors", !day.isInPeriod && "bg-gray-50 opacity-50", day.isInPeriod && isDayToday && "bg-blue-50 ring-2 ring-inset ring-blue-400", day.isInPeriod && !isDayToday && hasHours && "bg-green-50/50", day.isInPeriod && !isDayToday && !hasHours && !isDayFuture && "bg-gray-50/50"), children: [_jsxs("div", { className: "flex items-center justify-between mb-1", children: [_jsx("span", { className: cn("text-sm font-medium", isDayToday && "text-blue-700", !day.isInPeriod && "text-gray-400", day.isInPeriod && !isDayToday && "text-gray-700"), children: format(day.date, 'd') }), day.hasIncomplete && day.isInPeriod && (_jsx("span", { title: "Active session", children: _jsx(Clock, { className: "h-3 w-3 text-amber-500" }) }))] }), day.isInPeriod && (_jsx("div", { className: "space-y-0.5", children: hasHours ? (_jsxs(_Fragment, { children: [_jsx("div", { className: cn("text-lg font-bold", hasOvertime ? "text-orange-600" : "text-green-700"), children: formatHours(day.totalHours) }), _jsx("div", { className: "text-xs text-gray-500", children: hasOvertime ? (_jsxs("span", { className: "text-orange-600", children: ["+", day.overtimeHours.toFixed(1), " OT"] })) : (_jsx("span", { children: "hrs" })) })] })) : (!isDayFuture && (_jsx("div", { className: "text-sm text-gray-400", children: "-" }))) }))] }, day.dateKey));
                            }) }, weekIndex))) })] }), _jsxs("div", { className: "flex items-center justify-center gap-6 text-xs text-gray-500", children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("div", { className: "w-3 h-3 rounded bg-green-50 border border-green-200" }), _jsx("span", { children: "Hours logged" })] }), _jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("div", { className: "w-3 h-3 rounded bg-orange-100 border border-orange-300" }), _jsx("span", { children: "Overtime day" })] }), _jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx(Clock, { className: "h-3 w-3 text-amber-500" }), _jsx("span", { children: "Active session" })] }), _jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("div", { className: "w-3 h-3 rounded bg-blue-100 ring-2 ring-blue-400" }), _jsx("span", { children: "Today" })] })] })] }));
}
